import { useCallback, useEffect, useRef, useState } from 'react'
import {
    ECOMMERCE_SEGMENT,
    ERP_SEGMENT,
    getSalesPersonMappings,
    getSalesPersons,
    saveSalesPersonMappings
} from './salesPersonApi'

export const createRow = (rowId, mapping) => ({
    rowId,
    erpSalesPersonId: (mapping && mapping.erpSalesPersonId) || '',
    erpSalesPersonCode: (mapping && mapping.erpSalesPersonCode) || '',
    ecommerceSalesPersonId: (mapping && mapping.ecommerceSalesPersonId) || '',
    ecommerceSalesPersonCode: (mapping && mapping.ecommerceSalesPersonCode) || '',
    isNew: Boolean(mapping && mapping.isNew)
})

export const isRowComplete = (row) =>
    row.erpSalesPersonCode !== '' && row.ecommerceSalesPersonCode !== ''

/**
 * Owns every piece of sales person mapping state: the two option lists, the editable
 * rows and the save lifecycle. Components below only render what this returns.
 */
const useSalesPersonMapping = (connectorId) => {
    const [erpSalesPersons, setErpSalesPersons] = useState([])
    const [ecommerceSalesPersons, setEcommerceSalesPersons] = useState([])
    const [rows, setRows] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [loadError, setLoadError] = useState('')
    const [isSaving, setIsSaving] = useState(false)
    const [saveStatus, setSaveStatus] = useState(null)

    const nextRowId = useRef(0)
    const isMounted = useRef(true)
    const loadedKey = useRef(null)

    const buildRow = useCallback((mapping) => {
        nextRowId.current += 1
        return createRow(nextRowId.current, mapping)
    }, [])

    useEffect(() => {
        isMounted.current = true
        return () => {
            isMounted.current = false
        }
    }, [])

    useEffect(() => {
        if (!connectorId) {
            setIsLoading(false)
            return
        }

        // React 18 StrictMode runs effects twice in development. This ref survives
        // that simulated remount, so each connector is fetched exactly once - no
        // duplicate or cancelled requests in the network tab.
        const key = String(connectorId)
        if (loadedKey.current === key) {
            return
        }
        loadedKey.current = key

        setIsLoading(true)
        setLoadError('')

        Promise.all([
            getSalesPersons(connectorId, ERP_SEGMENT),
            getSalesPersons(connectorId, ECOMMERCE_SEGMENT),
            getSalesPersonMappings(connectorId)
        ])
            .then(([erpOptions, ecommerceOptions, mappings]) => {
                if (!isMounted.current) {
                    return
                }
                setErpSalesPersons(erpOptions)
                setEcommerceSalesPersons(ecommerceOptions)
                setRows(mappings.map((mapping) => {
                    nextRowId.current += 1
                    return createRow(nextRowId.current, mapping)
                }))
                setIsLoading(false)
            })
            .catch((error) => {
                if (!isMounted.current) {
                    return
                }
                setLoadError('Unable to load the sales person details. Please try again.')
                setIsLoading(false)
                // eslint-disable-next-line no-console
                console.error('Sales person mapping load failed', error)
            })
    }, [connectorId])

    const addRow = useCallback((mapping) => {
        setSaveStatus(null)
        setRows((current) => [buildRow({ ...mapping, isNew: true }), ...current])
    }, [buildRow])

    const updateRow = useCallback((rowId, changes) => {
        setSaveStatus(null)
        setRows((current) => current.map((row) => (row.rowId === rowId ? { ...row, ...changes } : row)))
    }, [])

    const removeRow = useCallback((rowId) => {
        setSaveStatus(null)
        setRows((current) => current.filter((row) => row.rowId !== rowId))
    }, [])

    const submitMappings = useCallback(() => {
        const payload = rows.filter(isRowComplete)

        setIsSaving(true)
        setSaveStatus(null)

        return saveSalesPersonMappings(connectorId, payload)
            .then((data) => {
                if (!isMounted.current) {
                    return
                }
                const succeeded = Boolean(data && data.result)
                setRows((current) => (succeeded ? current.map((row) => ({ ...row, isNew: false })) : current))
                setSaveStatus({
                    result: succeeded,
                    message: (data && data.message)
                        || (succeeded
                            ? `${payload.length} sales person mapping${payload.length === 1 ? '' : 's'} saved successfully.`
                            : 'Unable to save the sales person mappings.')
                })
            })
            .catch((error) => {
                if (!isMounted.current) {
                    return
                }
                setSaveStatus({ result: false, message: 'Unable to save the sales person mappings.' })
                // eslint-disable-next-line no-console
                console.error('Sales person mapping save failed', error)
            })
            .finally(() => {
                if (isMounted.current) {
                    setIsSaving(false)
                }
            })
    }, [rows, connectorId])

    const dismissStatus = useCallback(() => setSaveStatus(null), [])

    return {
        erpSalesPersons,
        ecommerceSalesPersons,
        rows,
        isLoading,
        loadError,
        isSaving,
        saveStatus,
        addRow,
        updateRow,
        removeRow,
        submitMappings,
        dismissStatus
    }
}

export default useSalesPersonMapping
